import { z } from 'zod';

/**
 * SIL-1007 — the current Terms of Service version. A hardcoded code constant
 * (not a DB-backed version table — the spec rejects that as YAGNI at current
 * scale, see docs/superpowers/specs/2026-09-10-tos-clickwrap-gate-design.md).
 * Bumping this is a code change (PR + deploy), NOT a DB write, and makes
 * every existing user's most recent tos_acceptance row stale automatically:
 * clientAuthMiddleware always checks "does a row exist at the CURRENT
 * constant", so the hard block re-applies platform-wide on next request with
 * no backfill needed.
 *
 * Imported by both the API (apps/api/src/middleware/clientAuth.ts, the
 * /api/client/tos routes) and, later, the UI (SIL-1008).
 */
export const TOS_VERSION = '2026-09-10';

/**
 * Static ToS text served by GET /api/client/tos. Plain text/markdown - the
 * UI renders it as-is in a <pre>. Kept alongside TOS_VERSION so a version
 * bump and its text always land in the same commit.
 *
 * SIL-2046: this is the published Terms of Service (live at /terms, source
 * docs/legal/terms-of-service.md, mirrored in
 * apps/customer/src/components/legal/TermsOfServiceContent.tsx). Copy-only
 * change: TOS_VERSION was deliberately NOT bumped, so no user is forced to
 * re-accept.
 */
export const TOS_TEXT = `# Silicon Exchange, Corp Terms of Service

**Effective date:** October 1, 2026

## Scope of These Terms

These Terms are the baseline terms for general use of the SiliconX customer portal: signing in, organization and team administration, browsing your asset inventory, and viewing FMV estimates, forecasts, and reports. Digital Passport generation, the TrueRead probe, and the exchange/marketplace (listings, bids, offers, and sales) are covered by separate terms, which apply in addition to these Terms when you use those features and are presented to you before you do.

## 1. About These Terms

These Terms of Service (these "Terms") are between Silicon Exchange, Corp ("Silicon Exchange," "SiliconX," "we," "us," or "our") and the person or organization that creates or uses an account to access the SiliconX platform ("you").

By creating an account, clicking to accept these Terms, or otherwise using the Services, you agree to these Terms. If you do not agree, do not use the Services. "Services" means the SiliconX customer portal and related platform features covered by these Terms, excluding Digital Passport generation and the Exchange, which are governed by their own terms when you use those features.

## 2. Eligibility

The Services are intended for business and professional use by adults at least 18 years old who are able to form a binding contract. You may not use the Services if you are barred from doing so under applicable law, including because you are listed on, or owned or controlled by a person listed on, a government sanctions or denied-party list.

## 3. Accounts, Organizations, and Team Roles

### 3.1 Accounts and Authority

Accounts are created by invitation or a referral code SiliconX issues. If you create an account or accept these Terms on behalf of an organization, you represent that you are authorized to bind that organization, and the organization is responsible for all activity under its account.

### 3.2 Organizations and Team Roles

The Services support organization-level accounts with multiple individual users assigned roles that govern what each user can see and do within the organization's account. An organization's administrators are responsible for assigning roles appropriately and for all activity by users they add, including after that user's employment or engagement with the organization ends, until the organization removes that user's access.

### 3.3 Credentials

Accounts use email-based sign-in. You must keep your credentials confidential, must not share them, and must notify us promptly at support@siliconx.com if you suspect unauthorized access. You are responsible for activity under your credentials until you notify us and we have had a reasonable opportunity to act.

### 3.4 Accuracy of Account Information

You must keep your account and organization information accurate and complete. We may ask you to verify your identity, your organization's legal identity, or your authority, and may limit or suspend features until you do.

## 4. Acceptable Use

You may use the Services only for lawful purposes and in accordance with these Terms. You must not, and must not permit anyone else to:

- violate any law or regulation, or infringe or misappropriate the rights of any person;
- provide false, misleading, or fraudulent information, or impersonate any person or organization;
- access an account, asset record, or data you are not authorized to access, or attempt to bypass an access control, role restriction, or organization boundary;
- probe, scan, or interfere with the Services except through a security program we have authorized in writing;
- use automated means to extract data from the Services beyond the features we make available to you;
- reverse engineer, decompile, or attempt to derive the source code or non-public methods of the Services, including the FMV estimation and reporting tools; or
- rely on an FMV estimate, forecast, or report in a manner inconsistent with Section 6 below.

We may investigate suspected violations, remove content, restrict features, and cooperate with law enforcement and regulators.

## 5. Your Content and Licence to SiliconX

### 5.1 Ownership

You retain ownership of the information, files, and data you or your users submit to the Services in the ordinary course of platform use, including asset inventory records, notes, and uploaded documents ("Your Content"). As between you and SiliconX, you retain ownership of Your Content. This section does not cover Digital Passport data, which is addressed in the separate terms for that feature.

### 5.2 Licence to SiliconX

You grant SiliconX a worldwide, non-exclusive, royalty-free licence, for the duration of these Terms and as long afterward as needed for the purposes below, to host, store, reproduce, process, analyze, and display Your Content, and to create derivative works from it, in order to: (a) provide, maintain, and secure the Services, including asset inventory and reporting features; (b) generate FMV estimates, forecasts, and reports for you; (c) comply with law and enforce these Terms; and (d) create Aggregated Data as described in Section 5.3.

### 5.3 Aggregated and De-identified Data

SiliconX may create and use statistical, aggregated, or de-identified data derived from Your Content ("Aggregated Data"), including to build and improve FMV estimation and forecasting models. Aggregated Data will not identify you, your organization, or a specific asset as yours. SiliconX owns Aggregated Data.

### 5.4 Accuracy and Consent

You represent and warrant that you have all rights necessary to submit Your Content and grant the licence above, and that Your Content is accurate to the best of your knowledge.

## 6. FMV Estimates, Forecasts, and Reports Are Informational Only

The Services may show fair market value (FMV) estimates, forecasts, price trends, and related reports for hardware assets, generated from market data, comparable transactions, and modeling that SiliconX develops and updates from time to time.

You agree and acknowledge that an FMV estimate, forecast, or report:

- is a statistical estimate, not an appraisal, valuation, certification, warranty, or guarantee of any asset's actual market value, condition, remaining life, or resale price;
- is based on data and models that may be incomplete, delayed, or wrong, and that SiliconX does not independently verify every input;
- may differ materially from the price an asset actually achieves in a transaction; and
- is not a substitute for your own diligence, appraisal, or professional advice before a financial, accounting, insurance, tax, or transactional decision.

## 7. Intellectual Property

SiliconX and its licensors own all right, title, and interest in the Services, including the FMV estimation and reporting tools, the customer portal, and all related technology, excluding Your Content and other content you submit. No rights are granted to you except the limited right to access and use the Services as permitted by these Terms. All trademarks, logos, and the SiliconX name are the property of SiliconX.

## 8. Third-Party Services

The Services may rely on third-party infrastructure and services (for example, cloud hosting, identity providers, SMS delivery providers, and market-data sources). SiliconX is not responsible for the availability or performance of third-party services outside its control.

## 9. Privacy

Our collection and use of personal information in connection with the Services is described in our Privacy Policy (https://portfolio.siliconx.tech/privacy).

## 10. Disclaimers and Limitation of Liability

### 10.1 Disclaimers

THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE." TO THE MAXIMUM EXTENT PERMITTED BY LAW, SILICONX DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. SILICONX DOES NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT ANY FMV ESTIMATE, FORECAST, OR REPORT IS ACCURATE OR COMPLETE.

### 10.2 Limitation of Liability

TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL SILICONX BE LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS OR LOST DATA.

## 11. Suspension and Termination

We may suspend or terminate your access to the Services, or a specific account, if you violate these Terms, if required by law, or on notice for convenience after 30 days. You may stop using the Services at any time. Termination of your access under these Terms also ends your ability to use the Digital Passport and Exchange features, since both require an active account. Sections that by their nature should survive termination (including Sections 6, 7, and 10) survive.

## 12. Governing Law

These Terms are governed by the laws of the State of Delaware, without regard to its conflict-of-laws principles.

## 13. Changes to These Terms

We may update these Terms from time to time. We will provide notice of material changes as described in the Services, and continued use after the change takes effect constitutes acceptance. Material changes take effect 30 days after notice.

## 14. General

These Terms, together with the separate terms for Digital Passport and Exchange features (to the extent you use those features) and our Privacy Policy, are the entire agreement between you and SiliconX regarding general platform access, and supersede prior discussions on that subject. If any provision is found unenforceable, the remaining provisions remain in effect. These Terms are not assignable by you without our consent. No waiver of any provision is effective unless in writing.

## 15. Contact

Questions about these Terms or security concerns: support@siliconx.com.

## 16. SMS Terms

- **Program name:** SiliconX verification codes.
- **What we send:** one-time verification codes and related account notices, by SMS, to the mobile number you enter for buyer verification. We send messages only after you enter your number and request a code.
- **Message frequency:** varies based on your actions.
- **Cost:** message and data rates may apply.
- **Opt out and help:** reply STOP to opt out at any time, or HELP for help. You can also contact us at support@siliconx.com.
- **Carriers:** wireless carriers are not liable for delayed or undelivered messages.
- **Privacy:** see our Privacy Policy (https://portfolio.siliconx.tech/privacy) for how we handle your mobile number. Mobile numbers and SMS opt-in consent are not sold, rented, or shared with third parties or affiliates for marketing or promotional purposes.`;

/** GET /api/client/tos response shape. */
export const tosResponseSchema = z.object({
  version: z.string(),
  text: z.string(),
});
export type TosResponse = z.infer<typeof tosResponseSchema>;

/** POST /api/client/tos/accept response shape. */
export const tosAcceptResponseSchema = z.object({
  version: z.string(),
  acceptedAt: z.string(),
});
export type TosAcceptResponse = z.infer<typeof tosAcceptResponseSchema>;

/** Structured 403 body clientAuthMiddleware returns when the current ToS is unaccepted. */
export interface TosRequiredError {
  error: 'tos_required';
  version: string;
}
